// This file contains text constants used in several modules across the application.
// Add text constants that are meant to be used in multiple modules here.
// For example, cross-sell text constants, common text or error messages, etc.

import { RewardsAccount, RewardsCurrency } from "redmond";
import React from "react";
import { getConfigTenant, isCorpTenant } from "../utils/getTenant";
import { PATH_HOME_TERMS, PATH_TERMS_COMPLIMENTARY_NIGHT } from "./urlPaths";

// Common errors
export const SEARCH_AGAIN_BUTTON = "Search again";
export const GO_BACK_BUTTON = "Go back";
export const GENERIC_SEARCH_ERROR_TITLE =
  "Uh oh! We were unable to process your search request.";
export const GENERIC_ERROR_SUBTITLE = "Something went wrong.";

// Luxury Promotions marketting / awareness
export const COMPLIMENTARY_NIGHT_PROMOTION_TITLE = "Complimentary fourth night";
export const COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION = (
  showsPremierCollection: boolean
) => {
  const collections = showsPremierCollection
    ? "Premier and Lifestyle"
    : "Lifestyle";
  return isCorpTenant(getConfigTenant())
    ? `Extend your business trip—book a stay of four or more nights and get your fourth night free at select ${collections} Collection Hotels.`
    : `Extend your escape when you book an eligible stay of at least four nights at select ${collections} Collection Hotels.`;
};
export const COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT = "Explore properties";
export const getCompNightTermsUrl = () =>
  isCorpTenant(getConfigTenant())
    ? PATH_HOME_TERMS
    : PATH_TERMS_COMPLIMENTARY_NIGHT;

export const NOVEMBER_VACATION_RENTAL_OFFER_TITLE =
  "$200 off premium vacation rentals";
export const NOVEMBER_VACATION_RENTAL_OFFER_DESCRIPTION = (
  isVentureX: boolean
) =>
  `Enjoy $200 off on any eligible ${
    isVentureX ? "Premier or Lifestyle" : "Lifestyle"
  } vacation rental booking. Offer automatically applied at checkout.`;
export const NOVEMBER_VACATION_RENTAL_OFFER_BUTTON_TEXT = "Book now";
export const NOVEMBER_VACATION_RENTAL_OFFER_TERMS_SUFFIX =
  " | Expires December 31, 2025.";
/**
 * Generates a JSX element for displaying earn rate information.
 *
 * @param rewardsAccount - The rewards account containing earn rate multipliers
 * @param currency - The rewards currency type ("Miles" | "Cash"). Must match RewardsCurrency type.
 * @param uiVariant - The type of travel service ("hotels", "cars", or "flights")
 * @returns A JSX element displaying the earn rate message, or undefined if no earn rate exists
 *
 * @example
 * ```tsx
 * const earnTag = getEarnTag(rewardsAccount, "Miles", "hotels");
 * // Returns JSX like: <span>Earn <span className="font-bold">5X Miles</span> on hotels</span>
 * ```
 */
export const getEarnTag = (
  rewardsAccount: RewardsAccount,
  currency: RewardsCurrency,
  uiVariant: "hotels" | "cars" | "flights" | "vacation rentals"
): React.JSX.Element | undefined => {
  const earnRateMap = {
    hotels: rewardsAccount.earn.hotelsMultiplier,
    cars: rewardsAccount.earn.carsMultiplier,
    flights: rewardsAccount.earn.flightsMultiplier,
    "vacation rentals": rewardsAccount.earn.homesMultiplier,
  };
  const earnRate = earnRateMap[uiVariant];
  const multiplierMap = {
    Miles: "X",
    Cash: "%",
    Points: "X",
  };
  const multiplier = multiplierMap[currency];

  if (earnRate) {
    return (
      <span>
        Earn{" "}
        <span className="font-bold">
          {earnRate}
          {multiplier} {currency}
        </span>{" "}
        on {uiVariant}
      </span>
    );
  }

  return undefined;
};
